import {
  GenericTable,
  Modal,
  NewButton,
  NewButtonVariant,
  Spinner,
} from "@visiblethread/react-components";
import Flex, { DIRECTION_CLASS, GAP_CLASS } from "components/Flex";
import { modalTypes } from "constants/types";
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "slices/app/actions/modals";
import { selectFilters } from "slices/captureDashboard";
import {
  exportTopOpportunitiesByBestFitCsv,
  exportTopOpportunitiesByBestFitPdf,
} from "slices/topOpportunitiesByBestFit/actions";
import {
  fetchTotalPipeline,
  selectTotalPipelineData,
  selectTotalPipelineError,
  selectTotalPipelineLoading,
} from "slices/totalPipeline";
import { opportunitiesLabel } from "utils/opportunities";

import { createColumns } from "./columns";
import styles from "./TotalPipelineModal.module.scss";

const MODAL_TITLE = "Total Pipeline";
const EMPTY_MSG = "No tracked opportunities in this period";

const SUBHEADER_SUFFIX = "highest to lowest business alignment";

const TotalPipelineModal = () => {
  const dispatch = useDispatch();
  const filters = useSelector(selectFilters);
  const data = useSelector(selectTotalPipelineData);
  const isLoading = useSelector(selectTotalPipelineLoading);
  const error = useSelector(selectTotalPipelineError);

  useEffect(() => {
    dispatch(fetchTotalPipeline());
  }, [dispatch, filters]);

  const onDismiss = () => {
    dispatch(hideModal(modalTypes.TOTAL_PIPELINE));
  };

  const columns = useMemo(() => createColumns(), []);

  const totalCount = data?.totalCount ?? 0;
  const opportunities = data?.opportunities ?? [];

  return (
    <Modal
      backdrop
      centered
      className={`fade-in ${styles.modal}`}
      onDismiss={onDismiss}
      show
      size="lg"
    >
      <Modal.Header>{MODAL_TITLE}</Modal.Header>
      <Modal.Body>
        <div
          className={styles.totalPipelineModal}
          direction={DIRECTION_CLASS.column}
        >
          {error && (
            <div className={styles.totalPipelineModal__error} role="alert">
              {error}
            </div>
          )}
          {!error && (
            <div>
              <p className={styles.totalPipelineModal__subHeader}>
                {opportunitiesLabel(totalCount)} · {SUBHEADER_SUFFIX}
              </p>
              {isLoading && <Spinner />}
            </div>
          )}
          {!error && totalCount === 0 && !isLoading && (
            <p className={styles.totalPipelineModal__empty}>{EMPTY_MSG}</p>
          )}
          {!error && totalCount > 0 && (
            <div className={styles.totalPipelineModal__tableWrapper}>
              <GenericTable columns={columns} data={opportunities} />
            </div>
          )}
          {!error && (
            <Flex justifyContent="flex-end" gap={GAP_CLASS.GAP_200}>
              <NewButton
                disabled={totalCount === 0}
                onClick={() => dispatch(exportTopOpportunitiesByBestFitCsv())}
                variant={NewButtonVariant.l2}
              >
                Export CSV
              </NewButton>
              <NewButton
                disabled={totalCount === 0}
                onClick={() => dispatch(exportTopOpportunitiesByBestFitPdf())}
                variant={NewButtonVariant.l1}
              >
                Export PDF
              </NewButton>
            </Flex>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default TotalPipelineModal;
